import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Color } from "src/elements/Color/Color";
import { Flex } from "src/elements/Box/Box";
import { Icon } from "src/elements/Icon/Icon";
import { Text } from "src/elements/Text/Text";
import { SelectOption } from "src/elements/Select/store/types";
import { selectHeight, selectHeightSmall } from "src/elements/Select/store/constants";
import { InputTitle } from "src/elements/Input/components/InputTitle";
import { colorToRGBA } from "src/utils/colorToRgba";

const Container = styled(Flex)<{ $width?: string }>`
  position: relative;
  flex-direction: column;
  width: ${({ $width }) => $width || "180px"};
`;

const Trigger = styled(Flex)<{ $isOpen: boolean; $isDisabled: boolean; $size?: "small" }>`
  height: ${({ $size }) => ($size === "small" ? `${selectHeightSmall}px` : `${selectHeight}px`)};
  width: 100%;
  border: 1px solid ${({ $isDisabled }) => ($isDisabled ? Color.textDisabled : Color.spaceGray)};
  border-radius: 4px;
  padding: ${({ $size }) => ($size === "small" ? "0 3px" : "0 10px")};
  cursor: ${({ $isDisabled }) => ($isDisabled ? "default" : "pointer")};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? "none" : "auto")};
  align-items: center;
  justify-content: space-between;
  gap: ${({ $size }) => ($size === "small" ? "5px" : "10px")};
  background: ${Color.white};
  transition: box-shadow 100ms ease;

  &:hover {
    box-shadow: ${({ $isOpen, $isDisabled }) =>
      !$isOpen && !$isDisabled && `0px 0px 7px ${colorToRGBA(Color.spaceGray, 0.3)}`};
  }
`;

const Dropdown = styled(Flex)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  background: ${Color.white};
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 8px;
  box-shadow: 0 4px 16px ${colorToRGBA(Color.black, 0.12)};
  flex-direction: column;
  min-width: 220px;
  max-height: 280px;
  overflow: hidden;
`;

const OptionsList = styled(Flex)`
  flex-direction: column;
  overflow-y: auto;
  max-height: 240px;
`;

const OptionRow = styled(Flex)`
  padding: 8px 14px;
  cursor: pointer;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  &:hover {
    background: ${Color.lightGray};
  }
`;

const CheckboxBox = styled(Flex)<{ $isChecked: boolean }>`
  width: 18px;
  height: 18px;
  min-width: 18px;
  border: 2px solid ${({ $isChecked }) => ($isChecked ? Color.primary : Color.spaceGray)};
  border-radius: 3px;
  background: ${({ $isChecked }) => ($isChecked ? Color.primary : Color.white)};
  align-items: center;
  justify-content: center;
  transition: all 150ms ease;
`;

interface MultiSelectProps {
  options: SelectOption<string>[];
  selected: string[];
  onChange: (values: string[]) => void;
  title?: string;
  placeholder?: string;
  allLabel?: string;
  width?: string;
  size?: "small";
  isDisabled?: boolean;
}

export const MultiSelect = ({
  options,
  selected,
  onChange,
  title,
  placeholder,
  allLabel = "All",
  width,
  size,
  isDisabled = false,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const allSelected = options.length > 0 && selected.length === options.length;
  const isEmpty = selected.length === 0;

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  const triggerLabel = allSelected
    ? allLabel
    : isEmpty
      ? (placeholder ?? allLabel)
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label || selected[0]
        : `${selected.length} selected`;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <Container ref={containerRef} $width={width}>
      <InputTitle label={title} isActive={!isDisabled} isDisabled={isDisabled} />

      <Trigger
        $isOpen={isOpen}
        $isDisabled={isDisabled}
        $size={size}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <Text
          variant={size === "small" ? "caption1" : "body3"}
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          color={isDisabled ? Color.textDisabled : isEmpty ? Color.spaceGray : Color.textMain}
        >
          {triggerLabel}
        </Text>
        <Icon
          name="triangleDown"
          size={14}
          color={isDisabled ? Color.textDisabled : Color.primary}
        />
      </Trigger>

      {isOpen && !isDisabled && (
        <Dropdown>
          <OptionsList>
            <OptionRow onClick={toggleAll}>
              <CheckboxBox $isChecked={allSelected}>
                {allSelected && <Icon name="checkmark" size={12} color={Color.white} />}
              </CheckboxBox>
              <Text variant="body3">{allLabel}</Text>
            </OptionRow>

            {options.map((option) => (
              <OptionRow key={option.value} onClick={() => toggleOption(option.value)}>
                <CheckboxBox $isChecked={selected.includes(option.value)}>
                  {selected.includes(option.value) && (
                    <Icon name="checkmark" size={12} color={Color.white} />
                  )}
                </CheckboxBox>
                <Text variant="body3">{option.label}</Text>
              </OptionRow>
            ))}
          </OptionsList>
        </Dropdown>
      )}
    </Container>
  );
};
